'use client'
import { withLinks, withMentions } from 'app/_components/Editor/extensions'
import { ReadonlyMention } from 'app/_components/Editor/extensions/withMentions/ReadonlyMention'
import { cx } from 'lib/cx'
import { UrlParam } from 'lib/urlParam'
import { useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { createEditor, type Descendant, Node, NodeEntry } from 'slate'
import { withHistory } from 'slate-history'
import type { RenderElementProps, RenderLeafProps } from 'slate-react'
import { Editable, Slate, withReact } from 'slate-react'
import { twMerge } from 'tailwind-merge'

function Element({ attributes, children, element }: RenderElementProps) {
  switch (element.type) {
    case 'link':
      return <span {...attributes}>{children}</span>
    case 'mention':
      return (
        <ReadonlyMention attributes={attributes} element={element}>
          {children}
        </ReadonlyMention>
      )
    default:
      return (
        <p className="text-text-secondary" {...attributes}>
          {children}
        </p>
      )
  }
}

export const Leaf = ({
  attributes,
  children,
  leaf,
}: RenderLeafProps & { leaf: any }) => {
  return (
    <span
      className={cx('inline', leaf.highlight && 'bg-highlight')}
      {...attributes}
    >
      {children}
    </span>
  )
}

export type PreviewRTEProps = {
  initialValue: Descendant[]
  className?: string
  customElement?: typeof Element
  search?: string
}

export default function PreviewRichTextEditor(props: PreviewRTEProps) {
  const searchParams = useSearchParams()
  const editor = useMemo(
    () => withReact(withHistory(withLinks(withMentions(createEditor())))),
    [],
  )
  const search = props.search || searchParams.get(UrlParam.search)

  const decorate = useCallback(
    ([node, path]: NodeEntry) => {
      const ranges = []
      if (search && Node.descendants(node)) {
        const texts = [
          ...Node.descendants(node)
            .map(([it]) => ('text' in it ? it.text : null))
            .filter((text): text is NonNullable<typeof text> => Boolean(text)),
        ]
        const normalizedSearchString = search.toLowerCase()
        const str = texts.join('')
        const normalizedText = str.toLowerCase()
        const length = normalizedSearchString.length
        let start = normalizedText.indexOf(normalizedSearchString)
        let index = 0
        let iterated = 0
        while (start !== -1) {
          // Skip already iterated strings
          while (
            index < texts.length &&
            start >= iterated + texts[index]!.length
          ) {
            iterated = iterated + texts[index]!.length
            index++
          }
          // Find the index of array and relative position
          let offset = start - iterated
          let remaining = length
          while (index < texts.length && remaining > 0) {
            const currentText = texts[index]!
            const currentPath = [...path, index]
            const taken = Math.min(remaining, currentText.length - offset)
            ranges.push({
              anchor: { path: currentPath, offset },
              focus: { path: currentPath, offset: offset + taken },
              highlight: true,
            })
            remaining = remaining - taken
            if (remaining > 0) {
              iterated = iterated + currentText.length
              // Next block will be indexed from 0
              offset = 0
              index++
            }
          }
          // Looking for next search block
          start = str.indexOf(search, start + search.length)
        }
      }

      return ranges
    },
    [search],
  )

  return (
    <div className="w-full">
      <Slate editor={editor} initialValue={props.initialValue}>
        <div className="w-full">
          <Editable
            decorate={decorate}
            readOnly
            className={twMerge('line-clamp-5 bg-white', props.className)}
            renderElement={props.customElement || Element}
            renderLeaf={Leaf}
          />
        </div>
      </Slate>
    </div>
  )
}
